// This is the venue plan for the internet sale
// It uses the GestureVenuePlan engine in our js-libs

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GestureVenuePlan } from "mobile-venue-plan-js";
import { assign, map } from "lodash";
import { $ } from "../../shortcut";
import { ContextMenuSettings } from 'mobile-venue-plan-js/lib/context-menu-control';
import { PlanSeat } from 'mobile-venue-plan-js/lib/plan-seat';
import { TicketModel } from '../../mvcapi/models/buyticket/submodels';
import { toMap } from '../../logic/util';
import { SeatAvailabilityModel, SectionAvailabilityModel, VenueSeatAvailabilityReportModel } from '../../tsrestapi/models/venue-seat-availability-report-model';
import { PlanSection } from 'mobile-venue-plan-js/lib/plan-section';
import { Rect, Vector2 } from 'peet-math';
import TranslateManager from 'ts-translate-js';
import "./venueplan.scss";
import { useIFrameResize } from '../../logic/hooks/utils';
import { VenuePlanControl } from '../../logic/reactcontext/venue-plan-control-context';


const tr = TranslateManager.boundTR;
const trN = tr;


function is_touch_device() {
  return 'ontouchstart' in window        // works on most browsers 
      || navigator.maxTouchPoints;       // works on IE10/11 and Surface
}

export type ServerVector2 = {
    X: number,
    Y: number
}

export type ServerRect = {
    P1: ServerVector2,
    Size: ServerSize2
}

export type ServerSize2 = {
    Width: number,
    Height: number
}

/*
export type Ticket = {
    Position: ServerVector2,
    IsAvailable: boolean,
    Name: string
};
*/

export type PlanInfo = {
    sceneLocation?: ServerVector2;
};

const _serverVector2ToJSV2 = (sV2: ServerVector2) => new Vector2(sV2.X, sV2.Y);
const _serverRectToJSRect = (sRect: ServerRect) => new Rect(sRect.P1.X, sRect.P1.Y, sRect.Size.Width, sRect.Size.Height);

export interface VenuePlanProps {
    //TODO: This seems unused anywhre. Remove it?
    eventDateId: number;
    tickets: TicketModel[];
    planInfo: PlanInfo;
    onSeatClick?: (ticketId: number) => void;
    onSectionClick?: (section: PlanSection) => void;
    contextMenu: ContextMenuSettings; // From MobileVenuePlanJS
    venueImageURL: string;
    engineSettings: any; // Settings passed directly to the underlying GetureVenuePlanEngine
    onPlanLoaded?: () => void;
    processing: boolean;
    autoFocusSeats?: boolean;
    defaultSeatSize?: number;
    availableSeats?: VenueSeatAvailabilityReportModel;
    remoteControl: VenuePlanControl;
    showHowToUsePlanShown?: boolean;
    //forceNoSeatMode?: boolean; // Tells the plan to not show seats at all
}

export function VenuePlan({ tickets, planInfo, onSeatClick, remoteControl, onSectionClick, autoFocusSeats = true, onPlanLoaded, availableSeats, contextMenu, venueImageURL, engineSettings, processing, showHowToUsePlanShown = true}: VenuePlanProps) {
    const selectedTickets = tickets; // Temp legacy Alias
    const planEngine = useRef<GestureVenuePlan>(null);
    const planContainer = useRef(null);
    const [ showScrollHandle, setShowScrollHandle ] = useState(false);
    const [ howToUsePlanShown, setHowToUsePlanShown ] = useState(showHowToUsePlanShown);
    const [ planEngineLoaded, setPlanEngineLoaded ] = useState(false); // After the plan engine is loaded
    const [ engineAndSeatLoaded, setEngineAndSeatLoaded ] = useState(false); // After the plan engine and the seats are loaded
    const [ afterLoadExecuted, setAfterLoadExecuted ] = useState(false); // When the after load is executed (once)

    const allVenueSeats = useMemo(() => availableSeats?.seats ? availableSeats?.seats : [], [availableSeats]);
    const gaSectionsWorthClickingOn = useMemo(() => availableSeats?.gaSections ? availableSeats?.gaSections : [], [availableSeats]);
    const showWaitSpinner = processing;
    const clickableSeatMode = availableSeats != null;

    useIFrameResize();

    useEffect(() => {
        if(!processing && !engineAndSeatLoaded && planEngineLoaded)
            setEngineAndSeatLoaded(true);
    }, [processing, engineAndSeatLoaded, planEngineLoaded, availableSeats, setEngineAndSeatLoaded]);

    const handlePlanLongClick = useCallback((at) => { planEngine.current.showContextMenuAt(at); }, []);
    const handlePlanSingleClick = useCallback((at) => { planEngine.current.showContextMenuAt(at); }, []);
    const handlePlanRightClick = useCallback((at) => { planEngine.current.showContextMenuAt(at); }, []);

    const handleSeatClick = useCallback((seat: PlanSeat) => { if(onSeatClick) onSeatClick(seat.id); }, [onSeatClick]);
    const handleSectionClick = useCallback((section: PlanSection) => { if(onSectionClick) onSectionClick(section); }, [onSectionClick]);
    
    const _propSelectedTicketToPlanSeat = (selectedTicket: TicketModel) => {
        return assign({}, _serverVector2ToJSV2(selectedTicket.Position), { id: selectedTicket.TicketID, size: selectedTicket.Size, state: "S" }) as PlanSeat;
    };

    const _propSeatToPlanSeat = (seat: SeatAvailabilityModel) => {
        return assign({}, _serverVector2ToJSV2(seat.pos.Center), { id: seat.id, size: seat.pos.Radius * 2, state: seat.avail ? "A" : "U" }) as PlanSeat;
    };

    const _beSectionToPlanSection = (section: SectionAvailabilityModel): PlanSection => {
        //return assign({}, _serverVector2ToJSV2(seat.Position.Center), { id: seat.TicketID, size: seat.Position.Radius * 2, state: seat.IsAvailable ? "A" : "U" }) as PlanSeat;

        return {
            id: section.sectionID,
            name: section.name, // TODO: Add the damn name here!
            remainingSeats: section.ticketsAvailable,
            isClickable: true, // TODO: Not used?
            position: _serverRectToJSRect(section.position).getCenter(),
            region: map(section.shape, _serverVector2ToJSV2), // TODO: Not used?
            size: 0 // TODO: Not used?
        };
    };

    const setPlanSeats = useCallback((_selectedTickets: TicketModel[] = []) => {
        const planSelectedTickets = tickets.filter((ticket) => ticket.Position).map(_propSelectedTicketToPlanSeat);
        const selTicketsById = toMap(planSelectedTickets, (s) => s.id);

        const seats = allVenueSeats
            .filter((seat) => !selTicketsById.has(seat.id) && seat.pos) // Remove the tickets that we know that we have selected
            .map(_propSeatToPlanSeat); // Convert to plan format

        planEngine.current.settings.clickableSeatMode = clickableSeatMode;

        planEngine.current.setSeats([ ...seats, ...planSelectedTickets ]);

        // Sections
        const sections = gaSectionsWorthClickingOn.map((section) => _beSectionToPlanSection(section));
        planEngine.current.setSections(sections);
    }, [allVenueSeats, tickets, clickableSeatMode, gaSectionsWorthClickingOn]);

    // Component load
    useEffect(() => {
        const planCtl = $(planContainer.current);
        planEngine.current = new GestureVenuePlan(planContainer.current, venueImageURL, engineSettings);
        planEngine.current.settings.clickableSeatMode = clickableSeatMode; // Activate this mode if we received seats. There might be a better way but we don't know yet.

        planEngine.current.onPlanLoaded = () => {
            console.log("Plan loaded");
            setPlanEngineLoaded(true);
        };

        setShowScrollHandle(is_touch_device() && (document.body.clientWidth - planCtl.width() < 40)); // This won't update on resizes but it's ok for the moment since this is for the small phone screen where we won't have the chance to have it bigger.
        
        // planEngine.current.onLongClick = clickableSeatMode ? null : handlePlanLongClick;
        // planEngine.current.onRightClick = clickableSeatMode ? null : handlePlanRightClick;
        planEngine.current.onSingleClick = handlePlanSingleClick; // This is a very single click, it won't fire on a double click
        
        // TODO: Est exécuté 40 fois. c'Est peut etre pour ça le trouble car ça stop l'engine

        planEngine.current.onSeatClick = handleSeatClick;
        planEngine.current.onSectionClick = handleSectionClick;
        
        planEngine.current.setContextMenu(contextMenu); // Not dynamic and cheap stuff. We must gradually convert the plan to a full react control...
        
        if(planInfo.sceneLocation !== null)
            planEngine.current.setEventLocation(_serverVector2ToJSV2(planInfo.sceneLocation));
        
        // Unload
        return () => {
            console.log("Plan stopped");
            planEngine.current.stop();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // This is an onLoad/onUnload

    useEffect(() => {
        if(planEngine.current) {
            planEngine.current.onLongClick = clickableSeatMode ? null : handlePlanLongClick;
            planEngine.current.onRightClick = clickableSeatMode ? null : handlePlanRightClick;
        }
    }, [planEngineLoaded, clickableSeatMode, handlePlanLongClick, handlePlanRightClick]);


    useEffect(() => { planEngine.current.onSeatClick = handleSeatClick; }, [handleSeatClick]);
    useEffect(() => { planEngine.current.onSectionClick = handleSectionClick; }, [handleSectionClick]);
    useEffect(() => { planEngine.current.onLongClick = clickableSeatMode ? null : handlePlanLongClick; }, [handlePlanLongClick, clickableSeatMode]);
    useEffect(() => { planEngine.current.onRightClick = clickableSeatMode ? null : handlePlanRightClick; }, [handlePlanRightClick, clickableSeatMode]);
    useEffect(() => { planEngine.current.onSingleClick = handlePlanSingleClick; }, [handlePlanSingleClick]);

    useEffect(() => {
        if(showWaitSpinner)
            planEngine.current.showWait();
        else
            planEngine.current.hideWait();
    }, [showWaitSpinner]);
    
    // On ticket changed (or plan loaded)
    useEffect(() => {
        if(!engineAndSeatLoaded) return;

        //const allPrevTicketsStr = reduce(previousTickets, (v, t) => v += "_" + t.Name, "");
        //const allNewTicketsStr = reduce(selectedTickets, (v, t) => v += "_" + t.Name, "");
        
        //if(allNewTicketsStr !== allPrevTicketsStr){
        setPlanSeats(selectedTickets);

        if(!remoteControl) {
            if(selectedTickets && selectedTickets.length > 0) { planEngine.current.moveToSeatsBySky(); }
            else { planEngine.current.moveToOverview(); }
        }
        //} else if(!prevEngineAndSeatLoaded) { // If it just loaded but with no tickets
        //    setPlanSeats(selectedTickets); // This also refreshes available seats
        //    planEngine.current.moveToOverview();
        //}
            
    }, [engineAndSeatLoaded, allVenueSeats, selectedTickets, remoteControl, setPlanSeats]);
    
    // On ticket changed (or plan loaded)
    useEffect(() => { // TODO : This will cause double move on a plan with no remote control
        // Move to seat on the very first load
        if(engineAndSeatLoaded && !afterLoadExecuted) {

            if(autoFocusSeats) {
                if(selectedTickets && selectedTickets.length > 0)
                    planEngine.current.moveToSeatsBySky();
                else
                    planEngine.current.moveToOverview();
            }

            setAfterLoadExecuted(true);
        } else if(!engineAndSeatLoaded && afterLoadExecuted) {
            setAfterLoadExecuted(false); // Reset this, the plan is unloaded
        }

    }, [engineAndSeatLoaded, allVenueSeats, selectedTickets, autoFocusSeats, afterLoadExecuted]);

    useEffect(() => {
        if(engineAndSeatLoaded && afterLoadExecuted && onPlanLoaded)
            onPlanLoaded();
    }, [engineAndSeatLoaded, afterLoadExecuted, onPlanLoaded]);

    // Remote control
    const handleMoveToSeatsBySky = useCallback(() => { planEngine.current.moveToSeatsBySky(); }, []);
    const handleMoveToSeats = useCallback(() => { planEngine.current.moveToSeats(); }, []);
    const handleMoveToXYZoomAtLeast = useCallback((center: {x: number, y: number}, zoom: number, timeMS: number) => { planEngine.current.moveToXYZoomAtLeast(center as any, zoom, timeMS); }, []);
    const handleMoveToOverview = useCallback(() => { planEngine.current.moveToOverview(); }, []);

    useEffect(() => {
        if(engineAndSeatLoaded && remoteControl) {
            remoteControl.onMoveToSeatsBySky(handleMoveToSeatsBySky);
            remoteControl.onMoveToSeats(handleMoveToSeats);
            remoteControl.onMoveToXYZoomAtLeast(handleMoveToXYZoomAtLeast);
            remoteControl.onMoveToOverview(handleMoveToOverview);
        }
        return () => {
            if(remoteControl) {
                remoteControl.offMoveToSeatsBySky(handleMoveToSeatsBySky);
                remoteControl.offMoveToSeats(handleMoveToSeats);
                remoteControl.offMoveToXYZoomAtLeast(handleMoveToXYZoomAtLeast);
                remoteControl.offMoveToOverview(handleMoveToOverview);
            }
        };
    }, [engineAndSeatLoaded, remoteControl, handleMoveToSeatsBySky, handleMoveToSeats, handleMoveToOverview, handleMoveToXYZoomAtLeast]);


    return (
        <div className="mobileVenuePlan">
            <div ref={planContainer} className="venueplan"></div>
            { showScrollHandle ? <VenuePlanScrollBand /> : null}
            {!clickableSeatMode && howToUsePlanShown
                ? <VenuePlanHowToUseToast 
                    ticketQTY={selectedTickets.length} 
                    clickableSeats={clickableSeatMode}
                    onToastHidden={() => setHowToUsePlanShown(false)} />
                : null}
            
        </div>
    );
}


export interface VenuePlanHowToUseToastProps {
    onToastShown?: () => void;
    onToastHidden?: () => void;
    clickableSeats: boolean;
    ticketQTY?: number;
}

// Depends on JQuery for the animation
function VenuePlanHowToUseToast({onToastShown, onToastHidden = null, clickableSeats, ticketQTY = 0 }: VenuePlanHowToUseToastProps) {
    const toastRef = useRef(null);

    const getInitialVPosition = () => {
        return -$(toastRef.current).height() - 20; // Position is based on it's height and is -20 to hide the shadow and to make it clearer
    };
    
    const show = useCallback(() => {
        const panel = $(toastRef.current);
        
        // Set the panel near the border
        panel.css({ top: getInitialVPosition() + "px" });

        // Make it appear slowly
        panel.animate({ top: "0px" }, 800, function() {
            if(onToastShown)
                onToastShown();
        }.bind(this));
    },[onToastShown]);

    const hide = () => {
        const panel = $(toastRef.current);
        
        // Make it disappear slowly
        panel.animate({ top: getInitialVPosition() + "px" }, 200, function() {
            if(onToastHidden)
                onToastHidden();
        }.bind(this));
    };

    const handleSeenClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        hide();
    };
    
    useEffect(() => {
        show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // This is an onLoad/onUnload


    let howToUse = "";
    if(!clickableSeats)
        howToUse = ticketQTY == 0 ? tr("Cliquez sur le plan pour chercher des sieges a l'endroit de votre choix.") : trN("Ce siege ne vous convient pas? Cliquez sur le plan pour chercher un billet a l'endroit de votre choix.", "Ces places ne vous conviennent pas? Cliquez sur le plan pour chercher des billets a l'endroit de votre choix.", { n: ticketQTY });
    else
         howToUse = tr("Cliquez sur les places que vous désirez réserver.");
    
        
    
    return <div ref={toastRef} className="message-toast" style={{ top: "-1000px" }}>
        <div className="message-toast-innerbox">
            {/* TODO: Refactor this as a button */}
            {/* eslint-disable-next-line sonarjs/anchor-is-valid */}
            <p><span className="fa fa-hand-pointer-o" aria-hidden="true"></span> {howToUse}  <a className="accented-text" href="#" onClick={handleSeenClick}>{tr("D'accord!")}</a></p>
        </div>
    </div>;
}


export interface VenuePlanScrollBandProps {
}
// Depends on JQuery for the animation
// This is a little band at the right of the plan that help the
// user to scroll the page when used on a small screen
function VenuePlanScrollBand() {
    const barRef = useRef(null);

    const getInitialHPosition = () => {
        return -$(barRef.current).width() - 20; // Position is based on it's height and is -20 to hide the shadow and to make it clearer
    };

    const show = () => {
        let panel = $(barRef.current);
        
        // Set the panel near the border
        panel.css({ right: getInitialHPosition() + "px" });

        // Make it appear slowly
        panel.animate({ right: "0px" }, 800);
    };

    useEffect(() => {
        show();
    }, []);
    
    return <div ref={barRef} className="scroll-handle" style={{ right: "-1000px" }}>
        {/* <div className="inner-label top-arrows">
            <i className="fa fa-bars"></i><br />
            <i className="fa fa-bars"></i>
        </div>
        
        <div className="inner-label center-grip">                
            <i className="fa fa-bars"></i><br />
            <i className="fa fa-bars"></i>
        </div>
        
        <div className="inner-label bottom-arrows">
            <i className="fa fa-bars"></i><br />
            <i className="fa fa-bars"></i>
        </div> */}
    </div>;
}


